import {
  inverseChainData,
  inverseConfigFor,
  inverseLendersByChain,
} from '@1delta/data-sdk'
import { InverseOracleAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { inverseLenderKey } from '../../../lending/public-data/inverse/convertPublic'

/**
 * Inverse Finance FiRM oracle price fetcher.
 *
 * One `Oracle.viewPrice(collToken, collateralFactorBps)` per market — the
 * PESSIMISTIC price (the ~2-day low when CF > 0), i.e. exactly what FiRM
 * uses for credit/withdrawal limits, which is what the per-market
 * `oraclePrice` field should carry. It is deliberately a LAGGING,
 * conservative read — the aggregation's freshness/deviation tiers handle
 * that like any other dampened lender feed.
 *
 * SCALE (live-verified 2026-08-04 against the protocol API): `viewPrice`
 * returns the DOLA(≈USD) value scaled so that `balance * price / 1e18` is
 * 1e18-DOLA — i.e. `10^(36 − collDecimals)` per whole token. WETH raw/1e18
 * = 1,856 while WBTC needs /1e28 (raw/1e18 would read 635 TRILLION), and
 * cbBTC matched the API to 10 decimals under this formula. Never divide by
 * a bare 1e18.
 *
 * Direct USD (PASS 1) → feeds the shared `usdPrices` map. DOLA is emitted
 * pegged to $1 (matching `convertPublic`, same as River's satUSD).
 */

interface InverseMarketMeta {
  lender: string
  chainId: string
  market: string
  collToken: string
  collDecimals: number
  dola?: string
}

/** Build one `viewPrice()` call per market across the FiRM deployment. */
export function getInverseCalls(
  chainId: string,
): FetcherResult<InverseMarketMeta>[] {
  const results: FetcherResult<InverseMarketMeta>[] = []

  for (const lender of inverseLendersByChain(chainId)) {
    const config = inverseConfigFor(lender, chainId)
    const markets = inverseChainData(lender, chainId)?.markets ?? []
    if (!config?.oracle || markets.length === 0) continue

    for (const m of markets) {
      if (!m.address || !m.collToken) continue
      const calls: MulticallCall[] = [
        {
          address: config.oracle,
          name: 'viewPrice',
          params: [m.collToken, BigInt(m.collateralFactorBps)],
        },
      ]
      results.push({
        calls,
        meta: {
          lender,
          chainId,
          market: m.address.toLowerCase(),
          collToken: m.collToken.toLowerCase(),
          collDecimals: m.collDecimals,
          dola: config.dola?.toLowerCase(),
        },
        lender: inverseLenderKey(lender, m.address),
      })
    }
  }

  return results
}

/**
 * Parse a market's `viewPrice` into direct-USD entries: the collateral at
 * the pessimistic-oracle USD price (10^(36 − collDecimals) normalization),
 * and DOLA pegged to $1. Keyed under the per-market `INVERSE_<ADDR>` lender.
 */
export function parseInverseResults(
  data: any[],
  meta: InverseMarketMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = inverseLenderKey(meta.lender, meta.market)
  const entries: OraclePriceEntry[] = []

  const raw = data?.[0]
  const price =
    typeof raw === 'bigint'
      ? raw
      : raw != null && raw !== '0x'
        ? BigInt(raw)
        : 0n
  // viewPrice scale is 10^(36 − collDecimals) per whole token — verified
  // live (see header). Same sanity band as the Liquity/River fetchers so a
  // mis-scaled read cannot pollute the shared price map.
  const collUSD = Number(price) / 10 ** (36 - meta.collDecimals)
  if (collUSD > 0 && collUSD < 1e9) {
    entries.push({
      asset: meta.collToken,
      price: collUSD,
      priceUSD: collUSD,
      marketUid: createMarketUid(chainId, key, meta.collToken),
      targetLender: key,
      description: 'Inverse FiRM pessimistic oracle (USD, ~2-day low)',
    })
  }

  if (meta.dola) {
    entries.push({
      asset: meta.dola,
      price: 1,
      priceUSD: 1,
      marketUid: createMarketUid(chainId, key, meta.dola),
      targetLender: key,
      description: 'Inverse DOLA (par)',
      staticBase: true,
      baseAsset: meta.dola,
    })
  }

  return entries
}

/** ABI for the Inverse oracle call (`viewPrice`). */
export function getInverseAbi() {
  return InverseOracleAbi
}

/** Inverse FiRM Oracle Fetcher module. */
export const inverseFetcher = {
  getCalls: getInverseCalls,
  parse: parseInverseResults,
  getAbi: getInverseAbi,
}
